import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { usePagination } from '@/lib/v3/components/Table';
import { OrderColumnState } from '@/lib/v3/components/Table/Table';

import {
  GetJourneyMessagePayload,
  JourneyMessageSortField,
  JourneyMessageTemplatesStatus,
} from '@/src/infrastructure/interfaces/IAutomation.interface';
import { useAutomationService } from '@/src/infrastructure/Protocol/Automation/useAutomationService';
import { CheckboxItems } from '@/src/modules/AutomationModuleNew/components/DropdownFilterSection/DropdownFilterSection';
import { JourneysMessagesTable } from '@/src/modules/AutomationModuleNew/interfaces/automation';

const defaultFilters: GetJourneyMessagePayload = {
  search: '',
  status: [],
  page: 1,
  limit: 10,
  creationDateBefore: null,
  creationDateAfter: null,
  sortField: 'creationDate',
  sortOrder: 'desc',
};

export const useJourneysMessageFilters = () => {
  const service = useAutomationService();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [countDataList, setCountDataList] = useState<{ total: number; partial: number }>({
    total: 0,
    partial: 0,
  });
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);

  const defaultDropDownItems = useCallback(
    (statusUrl?: string | null): CheckboxItems => {
      const statusUrlSplit = statusUrl?.split(',') as JourneyMessageTemplatesStatus[];
      return {
        status: {
          label: t('RecurringMessagesFilters.filter.status'),
          items: [
            {
              id: 'active',
              label: t('useJourneysMessageFilters.filter.active'),
              value: 'active',
              checked: statusUrlSplit?.includes('active') || false,
            },
            {
              id: 'inactive',
              label: t('useJourneysMessageFilters.filter.inactive'),
              value: 'inactive',
              checked: statusUrlSplit?.includes('inactive') || false,
            },
            {
              id: 'draft',
              label: t('useJourneysMessageFilters.filter.draft'),
              value: 'draft',
              checked: statusUrlSplit?.includes('draft') || false,
            },
          ],
        },
      };
    },
    [t]
  );

  const [dropDownItems, setDropDownItems] = useState<CheckboxItems>(
    defaultDropDownItems(searchParams.get('status'))
  );

  const {
    data,
    setData,
    isLoading,
    valuePagination,
    setRowCount,
    rowCount,
    paginationChange,
    orderColumn,
    orderColumnChange,
    rowSelection,
    rowSelectionChange,
  } = usePagination<JourneysMessagesTable>({
    initialPaginationState: {
      currentPage: searchParams.get('page')
        ? Number(searchParams.get('page'))
        : defaultFilters.page,
      pageSize: searchParams.get('limit')
        ? Number(searchParams.get('limit'))
        : (defaultFilters.limit as number),
    },
    initialOrderColumn: {
      orderByColumn: searchParams.get('sortField') || (defaultFilters.sortField as string),
      orderByDirection:
        (searchParams.get('sortOrder') as 'asc' | 'desc') ||
        (defaultFilters.sortOrder as 'asc' | 'desc'),
    },
  });

  const [filters, setFilters] = useState<GetJourneyMessagePayload>({
    search: searchParams.get('search') || '',
    status: searchParams.get('status')?.split(',') as JourneyMessageTemplatesStatus[],
    ...(searchParams.get('creationDateBefore') && {
      creationDateBefore: new Date(searchParams.get('creationDateBefore') as string),
    }),
    ...(searchParams.get('creationDateAfter') && {
      creationDateAfter: new Date(searchParams.get('creationDateAfter') as string),
    }),
    page: valuePagination.currentPage,
    limit: valuePagination.pageSize,
    sortField:
      searchParams.get('sortField') === 'createdDate' || searchParams.get('sortField') === null
        ? 'creationDate'
        : (searchParams.get('sortField') as JourneyMessageSortField),
    sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });

  const getHasFiltersApplied = useCallback((filters: GetJourneyMessagePayload) => {
    return (
      filters.search !== '' ||
      (filters.status && filters.status.length > 0) ||
      filters.creationDateBefore instanceof Date ||
      filters.creationDateAfter instanceof Date
    );
  }, []);

  const getFetchRecurrentMessage = useCallback(
    async (filters: GetJourneyMessagePayload) => {
      const response = await service.getJourneysMessages(filters);
      if (response.success) {
        const partial = response.data.pager.countPartial ?? 0;
        const total = response.data.pager.countTotal ?? 0;
        const hasFiltersApplied = getHasFiltersApplied(filters);
        setHasFiltersApplied(hasFiltersApplied);
        setCountDataList({ total, partial });
        setData(response.data.result);
        setRowCount(hasFiltersApplied ? partial : total);
      }
    },
    [getHasFiltersApplied, service, setData, setRowCount]
  );

  const handlePaginationChange = (currentPage: number, pageSize: number) => {
    void paginationChange(currentPage, pageSize, async () => {
      const newFilters = { ...filters, page: currentPage, limit: pageSize };
      updateFiltersUrl(newFilters);
      await getFetchRecurrentMessage(newFilters);
    });
  };

  const updateFiltersUrl = useCallback(
    (newFilters: GetJourneyMessagePayload) => {
      const updatedFilters = { ...filters, ...newFilters };
      setFilters(updatedFilters);

      const mapDates = {
        ...updatedFilters,
        creationDateBefore: updatedFilters.creationDateBefore?.toISOString(),
        creationDateAfter: updatedFilters.creationDateAfter?.toISOString(),
      };
      const params = new URLSearchParams();
      Object.entries(mapDates).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) params.set(key, value.join(','));
        } else if (value) {
          params.set(key, String(value));
        }
      });

      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  const updateUrlAndFetchDataWithResetPagination = useCallback(
    async (updateFilters: GetJourneyMessagePayload, noResetPageAndLimit: boolean = false) => {
      const newFilters = {
        ...filters,
        ...updateFilters,
        ...(!noResetPageAndLimit && { page: defaultFilters.page }),
        ...(!noResetPageAndLimit && { limit: defaultFilters.limit }),
      };
      updateFiltersUrl(newFilters);
      await paginationChange(newFilters.page as number, newFilters.limit as number, async () => {
        await getFetchRecurrentMessage(newFilters);
      });
    },
    [filters, getFetchRecurrentMessage, paginationChange, updateFiltersUrl]
  );

  const getInitialData = useCallback(async () => {
    await updateUrlAndFetchDataWithResetPagination(filters);
  }, [filters, updateUrlAndFetchDataWithResetPagination]);

  const resetFilters = useCallback(async () => {
    await updateUrlAndFetchDataWithResetPagination(defaultFilters);
    setDropDownItems(defaultDropDownItems());
  }, [defaultDropDownItems, updateUrlAndFetchDataWithResetPagination]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters((prevState) => ({ ...prevState, search: value }));
  }, []);

  const handleSearchApply = useCallback(
    (value: string) => {
      const newFilters = {
        ...filters,
        search: value,
      };
      void updateUrlAndFetchDataWithResetPagination(newFilters);
    },
    [filters, updateUrlAndFetchDataWithResetPagination]
  );

  const handleStatusFilterApply = useCallback(
    (checkedItems?: CheckboxItems) => {
      const newFilters = {
        ...filters,
        status:
          (checkedItems?.status?.items.map((item) =>
            item.value.toString()
          ) as JourneyMessageTemplatesStatus[]) || [],
      };
      void updateUrlAndFetchDataWithResetPagination(newFilters);
    },
    [filters, updateUrlAndFetchDataWithResetPagination]
  );

  const handleStatusFilterChange = useCallback(
    (isChecked: boolean, section: string, index: number) => {
      setDropDownItems((prevState) => {
        const newItems = { ...prevState };
        newItems[section].items[index].checked = isChecked;
        return newItems;
      });
    },
    []
  );

  const handleDateChange = useCallback(
    (date: Date | [Date | null, Date | null] | null) => {
      if (Array.isArray(date)) {
        setFilters((prevState) => ({
          ...prevState,
          creationDateAfter: date[0],
          creationDateBefore: date[1],
        }));
        if (date[1]) {
          void updateUrlAndFetchDataWithResetPagination({
            ...filters,
            creationDateAfter: date[0],
            creationDateBefore: date[1],
          });
        }
      }
    },
    [filters, updateUrlAndFetchDataWithResetPagination]
  );

  const handleOrderColumnChange = useCallback(
    (value: OrderColumnState) => {
      const newFilters = {
        ...filters,
        sortField: value.orderByColumn as JourneyMessageSortField,
        sortOrder: value.orderByDirection,
      };

      void orderColumnChange(value, async () => {
        await updateUrlAndFetchDataWithResetPagination(newFilters, true);
      });
    },
    [filters, orderColumnChange, updateUrlAndFetchDataWithResetPagination]
  );

  const totalRowSelected = Object.keys(rowSelection).length || 0;
  const dataSelected = Object.keys(rowSelection).map((index) => data[Number(index)]);

  return {
    dropDownItems,
    data,
    countDataList,
    getInitialData,
    valuePagination,
    rowCount,
    handlePaginationChange,
    isLoading,
    hasFiltersApplied,
    resetFilters,
    filters,
    handleSearchChange,
    handleSearchApply,
    handleStatusFilterApply,
    handleStatusFilterChange,
    handleDateChange,
    orderColumn,
    handleOrderColumnChange,
    rowSelection,
    rowSelectionChange,
    totalRowSelected,
    dataSelected,
  };
};
